import { useState, useEffect, createContext } from 'react';
import PropTypes from 'prop-types';
import { Prompt, useHistory } from 'react-router-dom';
import { useFunctionAsState } from '@/utils/hooks';
import { useContext } from 'react';
import { type LocationDescriptor } from 'history';
import { toast } from 'sonner';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';
import { Button } from '@/shadcn/button';
// Context creation with two default empty functions
// This will prevent errors if a component tries to use this context when it is not wrapped inside a Provider\

type ContextType = {
  setAskBeforeLeaving: (askBeforeLeaving: boolean) => void;
  setLeavingAlertModalConfig: (config: Record<string, any>) => void;
  setLeavingCallback: (leavingCallback: () => void) => void;
};

const BeforeLeavingAlertContext = createContext<ContextType>({
  setAskBeforeLeaving: () => {},
  setLeavingAlertModalConfig: () => {},
  setLeavingCallback: () => {}
});

BeforeLeavingAlertContext.displayName = 'BeforeLeavingAlertContext';

type ExpectedLocation = {
  location: LocationDescriptor;
  action: 'REPLACE';
};

export const BeforeLeavingAlertProvider = ({ children }) => {
  const history = useHistory();

  const [askBeforeLeaving, setAskBeforeLeaving] = useState(false);
  const [leavingAlertModalConfig, setLeavingAlertModalConfig] = useState<
    Record<string, any> | undefined
  >();
  const [leavingCallback, setLeavingCallback] = useFunctionAsState(() => {});
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const [expectedLocation, setExpectedLocation] = useState<ExpectedLocation>();
  const [modalVisible, setModalVisible] = useState(false);

  // Function  attached to react router's <Prompt> component
  const handleBlockedNavigation = (
    location: LocationDescriptor,
    action: any
  ) => {
    // If the navigation was not already confirmed and if the Modal config was defined, block the navigation and ask user for confirmation.
    if (!confirmedNavigation && !!leavingAlertModalConfig) {
      setModalVisible(true);
      setExpectedLocation({ location, action });
      return false;
    }
    // If not...
    // If the value of leavingCallback is a function, then execute it before leaving the page
    if (typeof leavingCallback === 'function') {
      leavingCallback();
    }
    return true;
  };

  // Modal confirm envent handler
  const handleConfirm = () => {
    setModalVisible(false);
    setConfirmedNavigation(true);
  };
  // Modal cancel envent handler
  const handleCancel = () => {
    setModalVisible(false);
    setConfirmedNavigation(false);
  };

  // Effect to handle navigation after user confirmation
  useEffect(() => {
    // If the navigation is confirmed by the user, navigate to the previous blocked location.
    if (confirmedNavigation && expectedLocation) {
      const { location, action } = expectedLocation;

      if (action === 'REPLACE') {
        // Use history.replace if the expected location change used it
        history.replace(location);
      } else {
        // If not (PUSH OR POP) use history.push by default
        // POP is when user uses browser history arrows, but since we cannot now details about it (back VS forward ? how many steps ?), we simply push a new entry in the history.
        history.push(location);
      }
    }
  }, [confirmedNavigation, expectedLocation]);

  // Event handler for strandard browser beforeunload event (triggered when the tab is refreshed, closed or links to an external URI)
  useEffect(() => {
    const beforeunloadHandler = event => {
      if (askBeforeLeaving) {
        event.preventDefault();
        event.returnValue = '';
        return event.returnValue;
      }
      return false;
    };
    const unloadHandler = () => {
      // If the value of leavingCallback is a function, then execute it before leaving the page
      if (typeof leavingCallback === 'function') {
        leavingCallback();
      }
    };
    window.addEventListener('beforeunload', beforeunloadHandler);
    window.addEventListener('unload', unloadHandler);
    return () => {
      window.removeEventListener('beforeunload', beforeunloadHandler);
      window.removeEventListener('unload', unloadHandler);
    };
  }, [askBeforeLeaving]);

  return (
    <BeforeLeavingAlertContext.Provider
      value={{
        setAskBeforeLeaving,
        setLeavingAlertModalConfig,
        setLeavingCallback
      }}
    >
      <Prompt when={!!askBeforeLeaving} message={handleBlockedNavigation} />

      <Dialog open={modalVisible} onOpenChange={handleCancel}>
        <DialogContent className="text-sm">
          <DialogHeader>
            <DialogTitle>
              {leavingAlertModalConfig?.title || 'Are you sure?'}
            </DialogTitle>
          </DialogHeader>
          {leavingAlertModalConfig?.content}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">
                {leavingAlertModalConfig?.cancelBtnText || 'Cancel'}
              </Button>
            </DialogClose>
            <Button
              onClick={handleConfirm}
              variant="destructive"
              data-cy="confirm-leaving-button"
            >
              {leavingAlertModalConfig?.confirmBtnText || 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {children}
    </BeforeLeavingAlertContext.Provider>
  );
};

BeforeLeavingAlertProvider.propTypes = {
  children: PropTypes.node.isRequired
};

// HOC function that will wrap the component inside BeforeLeavingAlertProvider
export const withLeavingAlertProvider = Component => props => (
  <BeforeLeavingAlertProvider>
    <Component {...props} />
  </BeforeLeavingAlertProvider>
);

// HOC function that will extend the component props by adding the context obect as props
// This means that the component will have 3 new props "setAskBeforeLeaving", "setLeavingAlertModalConfig" & "setLeavingCallback"
// allowing it to control the confirmation modal content, if the user should be asked for confirmation or not, and an optional callback executed after leaving
/** @deprecated use the `useBeforeLeavingAlert` hook instead. */
export const withLeavingAlertContext = Component => props => (
  <BeforeLeavingAlertContext.Consumer>
    {context => <Component {...props} {...context} />}
  </BeforeLeavingAlertContext.Consumer>
);

/**
 * @desc hook to get the before leaving alert context
 * @returns {ContextType} the before leaving alert context
 */
export const useBeforeLeavingAlert = () => {
  const context = useContext(BeforeLeavingAlertContext);
  if (!context) {
    toast.error(
      'useBeforeLeavingAlert must be used within a BeforeLeavingAlertProvider'
    );
    throw new Error(
      'useBeforeLeavingAlert must be used within a BeforeLeavingAlertProvider'
    );
  }
  return context;
};
